import { create } from 'zustand';
import { getOrganizations, createOrganization, updateOrganization, deleteOrganization } from '../services/organization.service';
import { OrganizationSchema, OrganizationRequest, UpdateOrganizationRequest, OrganizationPagination, OrganizationQueryParams } from '../types/organization.type';

interface OrganizationState {
  organizations: OrganizationSchema[];
  total: number;
  page: number;
  limit: number;
  selectedOrganization: OrganizationSchema | null;
  isLoading: boolean;
  error: string | null;
  showOrganizationModal: boolean;
  setSelectedOrganization: (organization: OrganizationSchema | null) => void;
  setShowOrganizationModal: (show: boolean) => void;
  getOrganizations: (params?: OrganizationQueryParams) => Promise<OrganizationPagination | null>;
  createOrganization: (organization: OrganizationRequest) => Promise<OrganizationSchema | null>;
  updateOrganization: (organizationId: string, organization: UpdateOrganizationRequest) => Promise<OrganizationSchema | null>;
  deleteOrganization: (organizationId: string) => Promise<boolean>;
}

interface GenericResponse<T> {
  error?: {
    message?: string;
    code?: string;
  } | null;
  response?: {
    message?: string;
    code?: string;
  } | null;
  result: T;
}

export const useOrganizationStore = create<OrganizationState>((set) => ({
  organizations: [],
  total: 0,
  page: 1,
  limit: 10,
  selectedOrganization: null,
  isLoading: false,
  error: null,
  showOrganizationModal: false,
  setShowOrganizationModal: (show: boolean) => set({ showOrganizationModal: show }),
  setSelectedOrganization: (organization) => set({ selectedOrganization: organization }),
  getOrganizations: async (params?: OrganizationQueryParams) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getOrganizations(params);
      const data = response as GenericResponse<OrganizationPagination>;
      set({
        organizations: data.result.organizations,
        total: data.result.total,
        page: data.result.page,
        limit: data.result.limit,
        isLoading: false
      });
      return data.result;
    } catch (error: any) {
      set({ error: error.response?.data?.["detail"] || 'Failed to get organizations', isLoading: false });
      return null;
    }
  },

  createOrganization: async (organization) => {
    set({ isLoading: true, error: null });
    try {
      const response = await createOrganization(organization);
      const data = response as GenericResponse<OrganizationSchema>;
      
      set((state) => ({ 
        organizations: [...state.organizations, data.result],
        isLoading: false 
      }));

      return data.result;
    } catch (error: any) {
      set({ error: error.response?.data?.["detail"] || 'Failed to create organization', isLoading: false });
      return null;
    }
  },

  updateOrganization: async (organizationId, organization) => {
    set({ isLoading: true, error: null });
    try {
      const response = await updateOrganization(organizationId, organization);
      const data = response as GenericResponse<OrganizationSchema>;
      
      set((state) => {
        const organizations = state.organizations.map((org) =>
          org.id === organizationId ? data.result : org,
        );
        const selectedOrganization =
          state.selectedOrganization?.id === organizationId
            ? data.result
            : state.selectedOrganization;
        if (selectedOrganization?.id === organizationId) {
          sessionStorage.setItem('selectedOrganization', JSON.stringify(data.result));
        }
        return { organizations, selectedOrganization, isLoading: false };
      });

      return data.result;
    } catch (error: any) {
      set({ error: error.response?.data?.["detail"] || 'Failed to update organization', isLoading: false });
      return null;
    }
  },

  deleteOrganization: async (organizationId) => {
    set({ isLoading: true, error: null });
    try {
      await deleteOrganization(organizationId);
      
      set((state) => ({ 
        organizations: state.organizations.filter(org => org.id !== organizationId),
        isLoading: false 
      }));

      return true;
    } catch (error: any) {
      set({ error: error.response?.data?.["detail"] || 'Failed to delete organization', isLoading: false });
      return false;
    }
  },
}));